export interface EventDefinition {
  // eslint-disable-next-line
  [key: string]: any;
}

export interface Events {
  [key: string]: EventDefinition;
}

export type EventHandler<T extends EventDefinition> = (
  payload: T,
) => Promise<void> | void;

export interface EventBus<T extends Events> {
  on<K extends keyof T>(
    event: K,
    eventHandler: EventHandler<T[K]>,
  ): EventBus<T>;
  once<K extends keyof T>(
    event: K,
    eventHandler: EventHandler<T[K]>,
  ): EventBus<T>;

  dispatch<K extends keyof T>(event: K, payload: T[K]): Promise<void> | void;
}

type EventOptions = {
  once?: boolean;
};

type EventItem = {
  eventHandler: EventHandler<EventDefinition>;
  options: EventOptions;
};

const EventDefaultOptions = {
  once: false,
};

class EventBusImpl<T extends Events> implements EventBus<T> {
  private events_: Map<keyof T, EventItem[]> = new Map();

  on<K extends keyof T>(event: K, eventHandler: EventHandler<T[K]>) {
    if (!this.events_.has(event)) {
      this.events_.set(event, []);
    }

    this.registerEvent(event, eventHandler);

    return this;
  }

  once<K extends keyof T>(event: K, eventHandler: EventHandler<T[K]>) {
    if (!this.events_.has(event)) {
      this.events_.set(event, []);
    }

    this.registerEvent(event, eventHandler, { once: true });

    return this;
  }

  async dispatch<K extends keyof T>(event: K, payload: T[K]) {
    const record = this.events_.get(event);

    if (record) {
      await Promise.all(
        record.map((i) => i.eventHandler(payload)),
      );

      this.events_.set(event, record.filter((i) => !i.options.once));
    }
  }

  private registerEvent<K extends keyof T>(
    event: K,
    eventHandler: EventHandler<T[K]>,
    options: EventOptions = EventDefaultOptions,
  ) {
    if (!this.events_.has(event)) {
      this.events_.set(event, []);
    }

    const record = this.events_.get(event);
    if (record) {
      record.push(
        { eventHandler: eventHandler as EventHandler<EventDefinition>, options },
      );
    }
  }
}

export const events = <T extends EventDefinition>(): EventBus<T> =>
  new EventBusImpl<T>();
